// Project overview cards of decision D23: production (tasks by state, production and elapsed time, tokens, cost, the
// share of done tasks and of work per agent, models), human decisions (pending, decided, how long people take to
// decide and how long until the task is back in production, channels, council reviews vs escalations), planning with
// agents (POST/GET planning runs) and the parallelism plan (waves that can run side by side).
import { useEffect, useState, type ReactNode } from "react";
import { useI18n } from "../../i18n/index.tsx";
import { getParallelism, getProjectApprovalMetrics, getProjectProduction, listPlanningRuns, startPlanningRun } from "../../api/endpoints-wave5.ts";
import type { PlanningRun } from "../../api/types-wave5.ts";
import { useAsync, type AsyncState } from "../../lib/useAsync.ts";
import { formatNumber } from "../../lib/format.ts";
import type { IconKind, Tone } from "../../lib/status.ts";
import { Button } from "../../components/Button.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ActivityIcon, GaugeIcon, LayersIcon, RefreshIcon, UserIcon } from "../../components/icons.tsx";
import { AgentsTable, CostText, ModelsTable, Stat, formatDuration, formatMinutes, labelOr } from "./common.tsx";

function InsightCard<T>({ id, icon, title, state, action, children }: { id: string; icon: ReactNode; title: string; state: AsyncState<T>; action?: ReactNode; children: (data: T) => ReactNode }) {
  const { t } = useI18n();
  return (
    <section className="card" aria-labelledby={id} data-testid={id}>
      <div className="card-header">
        {icon}
        <h2 id={id} className="card-title">
          {title}
        </h2>
        <span className="spacer" />
        {action}
      </div>
      <div className="card-body stack">
        {state.loading ? <LoadingBlock label={t("common.loading")} lines={3} /> : state.error && !state.data ? <ErrorBanner error={state.error} onRetry={state.reload} /> : state.data !== undefined ? children(state.data) : null}
      </div>
    </section>
  );
}

export function ProjectInsights({ projectId }: { projectId: string }) {
  const { t, locale } = useI18n();
  const production = useAsync((signal) => getProjectProduction(projectId, signal), [projectId]);
  const metrics = useAsync((signal) => getProjectApprovalMetrics(projectId, signal), [projectId]);
  const parallelism = useAsync((signal) => getParallelism(projectId, signal), [projectId]);
  const n = (v: number) => formatNumber(v, locale);
  const refreshAll = () => {
    production.reload();
    metrics.reload();
    parallelism.reload();
  };
  return (
    <div className="overview-insights stack" data-testid="project-insights">
      <div className="row">
        <h2 className="section-title">{t("insights.title")}</h2>
        <span className="spacer" />
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={refreshAll}>
          {t("insights.refresh")}
        </Button>
      </div>
      <div className="insights-grid">
        <InsightCard id="production-card" icon={<ActivityIcon size={18} />} title={t("production.projectTitle")} state={production}>
          {(p) => (
            <>
              <div className="stat-grid">
                <Stat
                  label={t("production.tasksDone")}
                  value={
                    // `done` counts built tasks only; tasks closed as already existing (D26) are reported apart.
                    p.tasks.existing > 0
                      ? t("production.doneOfExisting", { done: n(p.tasks.done + p.tasks.existing), total: n(p.tasks.total), existing: n(p.tasks.existing) })
                      : t("production.doneOf", { done: n(p.tasks.done), total: n(p.tasks.total) })
                  }
                  testId="production-done"
                />
                <Stat label={t("production.tasksWorking")} value={n(p.tasks.working)} />
                <Stat label={t("production.tasksAwaitingHuman")} value={n(p.tasks.awaiting_human)} />
                <Stat label={t("production.tasksBlocked")} value={n(p.tasks.blocked)} />
                <Stat label={t("production.productionTime")} value={formatDuration(p.production_ms)} />
                <Stat label={t("production.elapsed")} value={formatDuration(p.elapsed_ms)} />
                <Stat label={t("production.tokens")} value={t("production.tokensValue", { input: n(p.tokens.input), output: n(p.tokens.output) })} />
                <Stat label={t("production.cost")} value={<CostText cents={p.known_cost_cents} unknownCalls={p.unknown_cost_calls} />} />
              </div>
              <h3 className="subheading">{t("production.agents")}</h3>
              <AgentsTable agents={p.agents} label={t("production.agents")} shares />
              <h3 className="subheading">{t("production.models")}</h3>
              <ModelsTable models={p.models} label={t("production.models")} />
            </>
          )}
        </InsightCard>

        <InsightCard id="decisions-card" icon={<UserIcon size={18} />} title={t("decisions.title")} state={metrics}>
          {(m) => (
            <>
              <div className="stat-grid">
                <Stat label={t("decisions.pending")} value={n(m.pending)} testId="decisions-pending" />
                <Stat label={t("decisions.decided")} value={n(m.decided)} />
                <Stat label={t("decisions.timeToDecide")} value={t("decisions.medianP90", { median: formatMinutes(m.decision_minutes.median), p90: formatMinutes(m.decision_minutes.p90) })} />
                <Stat label={t("decisions.timeToResume")} value={t("decisions.medianP90", { median: formatMinutes(m.resume_minutes.median), p90: formatMinutes(m.resume_minutes.p90) })} />
              </div>
              <p data-testid="decisions-council">{t("decisions.council", { escalated: n(m.escalated_to_human), reviews: n(m.council_reviews) })}</p>
              {Object.keys(m.by_channel).length > 0 ? (
                <p className="row row--wrap">
                  <span className="subtle">{t("decisions.byChannel")}</span>
                  {Object.entries(m.by_channel).map(([channel, count]) => (
                    <span key={channel} className="badge">
                      {labelOr(t, `channel.${channel}`, channel)}: {n(count)}
                    </span>
                  ))}
                </p>
              ) : null}
              <p className="field-hint">{t("decisions.hint")}</p>
            </>
          )}
        </InsightCard>

        <PlanningCard projectId={projectId} onPlanned={parallelism.reload} />

        <InsightCard id="parallelism-card" icon={<LayersIcon size={18} />} title={t("parallelism.title")} state={parallelism}>
          {(p) => (
            <>
              <p data-testid="parallelism-summary">{t("parallelism.summary", { recommended: p.recommended_parallel, max: p.max_parallel, limit: p.wip_limit })}</p>
              <p className="subtle">{t("parallelism.facts", { waves: p.waves.length, critical: p.critical_path_length, blocked: p.blocked_by_dependencies })}</p>
              {(p.notes ?? []).length > 0 ? (
                <ul className="council-reasons" data-testid="parallelism-notes">
                  {p.notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              ) : null}
              {p.waves.length === 0 ? (
                <p className="subtle">{t("parallelism.none")}</p>
              ) : (
                <ol className="parallel-waves">
                  {p.waves.map((w) => (
                    <li key={w.index}>
                      <span className="subtle">{t("parallelism.wave", { n: w.index + 1 })}</span>{" "}
                      {w.tasks.map((task) => (
                        <span key={task.id} className="badge mono" title={task.title}>
                          #{task.number}
                        </span>
                      ))}
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </InsightCard>
      </div>
    </div>
  );
}

const RUN_VIEW: Record<PlanningRun["state"], { tone: Tone; icon: IconKind }> = {
  requested: { tone: "neutral", icon: "clock" },
  running: { tone: "info", icon: "play" },
  completed: { tone: "success", icon: "check" },
  failed: { tone: "danger", icon: "x" },
};

function PlanningCard({ projectId, onPlanned }: { projectId: string; onPlanned: () => void }) {
  const { t, formatRelative } = useI18n();
  const { toast } = useToast();
  const runs = useAsync((signal) => listPlanningRuns(projectId, signal), [projectId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const items = runs.data?.items ?? [];
  const active = items.some((r) => r.state === "requested" || r.state === "running");
  const { reload } = runs;
  // ponytail: polls every 5 s while a run is open; switch to the planning events when the API publishes them.
  useEffect(() => {
    if (!active) return;
    const id = window.setTimeout(reload, 5000);
    return () => window.clearTimeout(id);
  }, [active, runs.data, reload]);
  useEffect(() => {
    if (!active && items.length > 0) onPlanned();
    // Only when the last open run settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const plan = async () => {
    setBusy(true);
    setError(null);
    try {
      await startPlanningRun(projectId);
      toast(t("planning.requested"), "success");
      reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <InsightCard
      id="planning-card"
      icon={<GaugeIcon size={18} />}
      title={t("planning.title")}
      state={runs}
      action={
        <Button size="sm" variant="primary" loading={busy} disabled={active} onClick={() => void plan()} data-testid="planning-start">
          {t("planning.start")}
        </Button>
      }
    >
      {() => (
        <>
          <p className="field-hint">{t("planning.hint")}</p>
          <ActionError error={error} onDismiss={() => setError(null)} />
          {items.length === 0 ? (
            <p className="subtle">{t("planning.none")}</p>
          ) : (
            <ul className="hit-list">
              {items.map((r) => (
                <li key={r.id} className="hit" data-testid="planning-run" data-state={r.state}>
                  <div className="hit-head">
                    <Pill tone={RUN_VIEW[r.state].tone} icon={RUN_VIEW[r.state].icon}>
                      {t(`planning.state.${r.state}`)}
                    </Pill>
                    {r.mode ? <span className="badge">{t(`planning.mode.${r.mode}`)}</span> : null}
                    <span className="subtle">{formatRelative(r.created_at)}</span>
                  </div>
                  {r.state === "completed" ? <p>{t("planning.tasksCreated", { n: r.tasks_created })}</p> : null}
                  {r.error ? <p className="task-card-blocker">{r.error}</p> : null}
                  {r.warnings.length > 0 ? (
                    <ul className="council-reasons">
                      {r.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </InsightCard>
  );
}
